/**
 * LaunchDarkly Mock Client
 * 
 * Purpose: Enables REX component features without requiring LaunchDarkly connection
 * 
 * Background:
 * - REX components use @rco/rex-launchdarkly-lib for feature flags
 * - React requires mock to prevent REX components from waiting for LD initialization
 * 
 * IMPORTANT: This must be initialized BEFORE REX components load
 * 
 * @see main.tsx - Called during app bootstrap
 */

/**
 * Initialize LaunchDarkly mock client
 * Creates a mock implementation that returns predefined flag values
 * and intercepts LaunchDarkly API requests
 */
export function initLaunchDarklyMock(): void {
  /**
   * Feature flag configuration
   * Controls REX AI sidebar capabilities
   */
  const flagValues: Record<string, boolean> = {
    'rex-ai-sidebar-isHistoryEnabled': true,
    'rex-ai-sidebar-isInlineSuggestionsEnabled': true,
    'rex-ai-sidebar-isDropdownSuggestionsEnabled': true,
    'rex-ai-sidebar-isFileUploadEnabled': false,
  };

  /**
   * Mock LaunchDarkly client implementation
   * Provides necessary methods for REX component compatibility
   */
  const mockLDClient = {
    variation: <T>(flagKey: string, defaultValue: T): boolean | T => {
      const value = flagValues[flagKey] ?? defaultValue;
      console.log(`[LD Mock] variation("${flagKey}") = ${value}`);
      return value;
    },
    on: (event: string, callback: (value: boolean) => void) => {
      console.log(`[LD Mock] on("${event}")`);
      // Immediately trigger flag updates for change events
      if (event.startsWith('change:')) {
        const flagKey = event.substring(7);
        const value = flagValues[flagKey];
        if (value !== undefined) {
          setTimeout(() => callback(value), 0);
        }
      }
      return mockLDClient;
    },
    off: () => mockLDClient,
    allFlags: () => {
      console.log('[LD Mock] allFlags()', flagValues);
      return flagValues;
    },
    waitForInitialization: () => Promise.resolve(mockLDClient),
    waitUntilReady: () => Promise.resolve(),
    identify: () => Promise.resolve({}),
    close: () => Promise.resolve(),
  };

  /**
   * Intercept LaunchDarkly API requests
   * Prevents actual network calls and returns mock flag values
   */
  const originalFetch = window.fetch.bind(window);
  window.fetch = (...args: Parameters<typeof fetch>): ReturnType<typeof fetch> => {
    const url = args[0]?.toString?.() || '';
    // Block LaunchDarkly stream connections
    if (url.includes('launchdarkly.com')) {
      console.log('[LD Mock] Intercepted LaunchDarkly request:', url);
      return Promise.resolve(
        new Response(JSON.stringify(flagValues), {
          status: 200,
          headers: { 'Content-Type': 'application/json' },
        })
      );
    }
    return originalFetch(...args);
  };

  console.log('[LD Mock] LaunchDarkly mock initialized with flags:', flagValues);
}
